import axiosConfig from "../axiosConfig";

/**
 * Comment Service - 設計05 コメント/添付機能のAPIクライアント
 */

export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  "dwg",
  "dxf",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "zip",
];
export const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024; // 50MB
export const MAX_ATTACHMENTS_PER_COMMENT = 5;

export type CommentAttachment = {
  id: string;
  file_name: string;
  s3_key: string;
  file_size: number;
  url: string;
};

export type Comment = {
  id: string;
  order_id: string;
  ticket_id: string | null;
  account_id: string;
  body: string;
  visibility: "public" | "internal";
  kind: "comment" | "rejection_reason" | "order_rejection_reason";
  created_at: string;
  updated_at: string | null;
  author_name: string;
  author_type: "customer" | "employee" | "subcontractor";
  file_name: string | null;
  is_read: boolean;
  is_own: boolean;
  attachments: CommentAttachment[];
};

export type NewAttachment = {
  file_name: string;
  s3_key: string;
  file_size: number;
};

/** 案件単位（配下図面コメント含む）のコメントを取得 */
export const getCommentsByOrder = async (
  orderId: string
): Promise<Comment[]> => {
  const res = await axiosConfig.get("/comments", {
    params: { order_id: orderId },
  });
  return res.data.body.data;
};

/** 図面単位のコメントを取得 */
export const getCommentsByTicket = async (
  ticketId: string
): Promise<Comment[]> => {
  const res = await axiosConfig.get("/comments", {
    params: { ticket_id: ticketId },
  });
  return res.data.body.data;
};

export const createComment = async (payload: {
  order_id: string;
  ticket_id?: string | null;
  body: string;
  visibility?: "public" | "internal";
  kind?: string;
  attachments?: NewAttachment[];
}): Promise<string> => {
  const res = await axiosConfig.post("/comments", payload);
  return res.data.body.id;
};

export const updateComment = async (
  commentId: string,
  body: string
): Promise<void> => {
  await axiosConfig.patch(`/comments/${commentId}`, { body });
};

export const deleteComment = async (commentId: string): Promise<void> => {
  await axiosConfig.delete(`/comments/${commentId}`);
};

export const readCommentsBulk = async (
  commentIds: string[]
): Promise<void> => {
  if (!commentIds.length) return;
  await axiosConfig.post("/comments/read-bulk", { comment_ids: commentIds });
};

/**
 * 添付ファイルアップロード用の presigned URL を取得し、S3 に直接 PUT する。
 * 成功時、DB に登録するための { file_name, s3_key, file_size } を返す。
 */
export const uploadCommentAttachment = async (
  file: File
): Promise<NewAttachment> => {
  const res = await axiosConfig.post("/comments/attachment-presigned-url", {
    file_name: file.name,
    file_size: file.size,
  });
  const { url, s3_key } = res.data.body;
  await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": "application/octet-stream" },
  });
  return { file_name: file.name, s3_key, file_size: file.size };
};

/** FE 側の添付バリデーション（拡張子・サイズ・件数） */
export const validateAttachments = (files: File[]): string | null => {
  if (files.length > MAX_ATTACHMENTS_PER_COMMENT) {
    return "添付は1コメントあたり5ファイルまでです。";
  }
  for (const f of files) {
    const ext = f.name.includes(".")
      ? f.name.split(".").pop()!.toLowerCase()
      : "";
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      return `許可されていないファイル形式です: ${f.name}`;
    }
    if (f.size > MAX_ATTACHMENT_SIZE) {
      return `1ファイルの上限は50MBです: ${f.name}`;
    }
  }
  return null;
};
